import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Heart } from 'lucide-react';

export default function RegisterPage() {
  const [form, setForm] = useState({ email: '', password: '', full_name: '', role: 'client', phone: '' });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const result = await register(form);
      if (result.status === 'pending') {
        toast.success('Registration submitted! Your account is pending admin approval.');
        navigate('/login');
      } else {
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center">
            <Heart className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-teal-800" style={{ fontFamily: 'Manrope' }}>Purpose Driven</span>
        </div>

        <div className="bg-white rounded-2xl border border-border p-6 md:p-8 shadow-sm">
          <h2 className="text-xl font-bold mb-1 text-center" style={{ fontFamily: 'Manrope' }}>Create Account</h2>
          <p className="text-sm text-muted-foreground text-center mb-6">Join the Purpose Driven community</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Full Name</Label>
              <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} placeholder="Your full name" required className="h-11" data-testid="register-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" required className="h-11" data-testid="register-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Password</Label>
              <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Create a password" required className="h-11" data-testid="register-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">I am signing up as</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger className="h-11" data-testid="register-role-select">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Participant — HEAT / HER / Peer Support</SelectItem>
                  <SelectItem value="housing_resident">Resident — Our Square Transitional Housing</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground pt-0.5">
                Staff and administrator accounts are created by Purpose Driven leadership. If you're joining the team, please contact your supervisor.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Phone (optional)</Label>
              <Input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" className="h-11" data-testid="register-phone-input" />
            </div>
            <Button type="submit" className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-medium mt-2" disabled={loading} data-testid="register-submit-button">
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-4">
          Already have an account? <Link to="/login" className="text-teal-600 font-medium hover:underline" data-testid="login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
